// src/services/api/subscription.js
import { apiClient } from './base';

/**
 * Get all available subscription plans
 * @returns {Promise<Object>} List of subscription plans
 */
export const getAllPlans = async () => {
  try {
    const response = await apiClient.get('/subscriptions/plans');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get current subscription details
 * @returns {Promise<Object>} Current subscription
 */
export const getMySubscription = async () => {
  try {
    const response = await apiClient.get('/subscriptions/my-subscription');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get usage statistics and quota limits
 * @returns {Promise<Object>} Usage and limits data
 */
export const getMyUsage = async () => {
  try {
    const response = await apiClient.get('/subscriptions/usage');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get bank account details for bank transfers
 * @returns {Promise<Object>} Bank account information
 */
export const getBankAccountDetails = async () => {
  try {
    const response = await apiClient.get('/subscriptions/payment/bank-details');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Activate trial subscription during setup
 * @param {number} subscriptionPlanId - Plan ID to activate
 * @returns {Promise<Object>} Activated subscription details
 */
export const activateTrialSubscription = async (subscriptionPlanId) => {
  try {
    const response = await apiClient.post('/subscriptions/activate-trial', {
      subscriptionPlanId
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Initiate subscription payment
 * @param {number} planId - Plan ID
 * @param {string} billingCycle - Billing cycle (monthly/quarterly/yearly)
 * @param {string} paymentMethod - Payment method ('bank_transfer' or 'card_payment')
 * @param {File} paymentProof - Payment proof file (required for bank_transfer)
 * @returns {Promise<Object>} Payment initiation response
 */
export const initiateSubscriptionPayment = async (planId, billingCycle, paymentMethod, paymentProof = null) => {
  try {
    const formData = new FormData();
    formData.append('planId', planId);
    formData.append('billingCycle', billingCycle);
    formData.append('paymentMethod', paymentMethod);
    
    if (paymentMethod === 'bank_transfer' && paymentProof) {
      formData.append('paymentProof', paymentProof);
    }

    const response = await apiClient.post('/subscriptions/subscribe/payment', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Initiate upgrade payment
 * @param {number} planId - New plan ID
 * @param {string} paymentMethod - Payment method ('bank_transfer' or 'card_payment')
 * @param {File} paymentProof - Payment proof file (required for bank_transfer)
 * @returns {Promise<Object>} Payment initiation response
 */
export const initiateUpgradePayment = async (planId, paymentMethod, paymentProof = null) => {
  try {
    const formData = new FormData();
    formData.append('planId', planId);
    formData.append('paymentMethod', paymentMethod);
    
    if (paymentMethod === 'bank_transfer' && paymentProof) {
      formData.append('paymentProof', paymentProof);
    }

    const response = await apiClient.post('/subscriptions/upgrade/payment', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Check payment status
 * @param {number} paymentId - Payment ID
 * @returns {Promise<Object>} Payment status
 */
export const checkPaymentStatus = async (paymentId) => {
  try {
    const response = await apiClient.get(`/subscriptions/payment/${paymentId}/status`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Subscribe to a plan (deprecated - use activateTrialSubscription for setup)
 * @param {number} planId - Plan ID
 * @param {string} billingCycle - Billing cycle (monthly/quarterly/yearly)
 * @returns {Promise<Object>} Subscription details
 */
export const subscribeToPlan = async (planId, billingCycle = 'monthly') => {
  try {
    const response = await apiClient.post('/subscriptions/subscribe', {
      planId,
      billingCycle
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Upgrade to higher plan (deprecated - use initiateUpgradePayment instead)
 * @param {number} planId - New plan ID
 * @returns {Promise<Object>} Updated subscription
 */
export const upgradePlan = async (planId) => {
  try {
    const response = await apiClient.post('/subscriptions/upgrade', {
      planId
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Request downgrade (effective next billing cycle)
 * @param {number} planId - New plan ID
 * @returns {Promise<Object>} Updated subscription
 */
export const downgradePlan = async (planId) => {
  try {
    const response = await apiClient.post('/subscriptions/downgrade', {
      planId
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Cancel subscription
 * @param {string} reason - Cancellation reason
 * @returns {Promise<Object>} Cancelled subscription
 */
export const cancelSubscription = async (reason = '') => {
  try {
    const response = await apiClient.post('/subscriptions/cancel', {
      reason
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Reactivate cancelled subscription
 * @returns {Promise<Object>} Reactivated subscription
 */
export const reactivateSubscription = async () => {
  try {
    const response = await apiClient.post('/subscriptions/reactivate');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get payment history
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @returns {Promise<Object>} Paginated payment history
 */
export const getPaymentHistory = async (page = 1, limit = 10) => {
  try {
    const response = await apiClient.get('/subscriptions/payments', {
      params: { page, limit }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Preview next invoice
 * @returns {Promise<Object>} Next invoice details
 */
export const getNextInvoice = async () => {
  try {
    const response = await apiClient.get('/subscriptions/next-invoice');
    return response.data;
  } catch (error) {
    throw error;
  }
};